import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { DOCUMENTS_PENDING_FILTER, garageListParams, normalizeTermsFilter } from '../services/garageListFilter';
import { GarageResponse } from '../types';
import { useAuth } from '../hooks/useAuth';
import UsernameChip from '../components/UsernameChip';
import TermsNoticeSendDialog from '../components/garages/TermsNoticeSendDialog';
import TermsStatusBadge from '../components/garages/TermsStatusBadge';
import { TERMS_FILTER_OPTIONS, TERMS_NOTICE_SEND_SCREEN } from '../components/garages/termsNotice';

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'PENDING' ? 'pending'
    : status === 'APPROVED' ? 'approved'
    : status === 'REJECTED' ? 'rejected'
    : status === 'UPDATE_REQUESTED' ? 'update-requested'
    : 'pending';
  return <span className={`admin-badge ${cls}`}>{status.replace('_', ' ')}</span>;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'UPDATE_REQUESTED', label: 'Update Requested' },
  // #1101 — the dashboard's documents tile lands here; not a garage status (see garageListFilter).
  { value: DOCUMENTS_PENDING_FILTER, label: 'Documents to review' },
];

export default function GaragesPage() {
  const auth = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [garages, setGarages] = useState<GarageResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [termsFilter, setTermsFilter] = useState<string>(normalizeTermsFilter(searchParams.get('terms')));
  const [sendOpen, setSendOpen] = useState(false);
  const canSendNotices = auth.hasScreen(TERMS_NOTICE_SEND_SCREEN);

  const fetchGarages = useCallback(() => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    api.listGarages(auth.token, garageListParams(statusFilter, searchText, termsFilter))
      .then((data) => setGarages(data.garages))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.token, statusFilter, searchText, termsFilter]);

  useEffect(() => {
    const timeout = setTimeout(fetchGarages, 300);
    return () => clearTimeout(timeout);
  }, [fetchGarages]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter) params.status = statusFilter;
    if (termsFilter) params.terms = termsFilter;
    if (searchText.trim()) params.search = searchText.trim();
    setSearchParams(params, { replace: true });
  }, [statusFilter, termsFilter, searchText, setSearchParams]);

  return (
    <div>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', marginBottom: '1.25rem' }}>
        Garages {!loading && `(${garages.length})`}
      </h2>

      <div className="admin-card">
        <div className="admin-toolbar">
          <input
            type="text"
            className="admin-search-input"
            placeholder="Search by name, owner @username, license, email, or phone..."
            aria-label="Search garages"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <select
            className="admin-select"
            aria-label="Garage status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <select
            className="admin-select"
            aria-label="Terms"
            data-testid="terms-filter"
            value={termsFilter}
            onChange={(e) => setTermsFilter(e.target.value)}
          >
            {TERMS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {canSendNotices && (
            <button
              type="button"
              className="admin-btn admin-btn-outline signed-agreements-btn"
              onClick={() => setSendOpen(true)}
            >
              Send terms…
            </button>
          )}
        </div>

        {loading ? (
          <div className="admin-loading">
            <div className="admin-loading-spinner"></div>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : garages.length === 0 ? (
          <div className="admin-empty-state">
            <p>No garages found.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Garage Name</th>
                  <th>Owner</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Terms</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {garages.map((g) => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 500 }}>{g.name}</td>
                    <td><UsernameChip username={g.ownerUsername} userId={g.ownerUserId} compact /></td>
                    <td>{g.email || '-'}</td>
                    <td>{g.phone || '-'}</td>
                    <td><StatusBadge status={g.status} /></td>
                    <td><TermsStatusBadge status={g.termsStatus} /></td>
                    <td>{new Date(g.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Link to={`/garages/${g.id}`} className="admin-link">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sendOpen && (
        <TermsNoticeSendDialog onClose={() => setSendOpen(false)} onSent={fetchGarages} />
      )}
    </div>
  );
}
